import { connect, FormikValues } from 'formik'
import { cloneDeep, flattenDeep, get, isEmpty, isEqual, last, pullAllWith, sortBy, startsWith } from 'lodash'
import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import { Text } from '@/components/Form'
import { Row } from '@/components/StyledComponents'
import { StyledDivider, VariableInputContainer } from '@/pages/SpaceAppBuilder/components/StyledComponents'
import VariableAutoComplete from '@/pages/SpaceAppBuilder/components/VariableInputDropdown'
import { FieldTypes, Variable } from '@/pages/SpaceAppBuilder/utilities/interfaces'
import { getAllFlattenedVariables } from '@/pages/SpaceAppBuilder/utilities/variableHelpers'

import DataCollectionFieldRow from './DataCollectionFieldRow'
import DataCollectionFieldsAccordion from './DataCollectionFieldsAccordion'
import messages from './messages'
import { AnyContentBlockTypes } from './NotificationDetail'
import { Label } from './StyledComponents'

const StyledRow = styled(Row)`
  padding-right: 10px;
  margin-left: 10px;
`

const StyledVariableInputTitle = styled(Label)`
  padding-bottom: 10px;
`

const StyledFieldsTitle = styled(Label)`
  padding-top: 15px;
`

const StyledText = styled(Text)`
  width: 100%;
`

const StyledEmptyCollectionSet = styled.label`
  font-size: 0.875rem;
  color: #707070;
  display: block;
  margin-left: 5px;
  margin-top: 10px;
  margin-bottom: 0;
`

const StyledEmptyCollectionValidation = styled.div`
  margin-top: 10px;
`

const StyledVariableAutoComplete = styled(VariableAutoComplete)`
  && {
    &.form__filter-select {
      margin-left: 10px;
    }
  }
`

const StyledRequiredSpan = styled.span`
  color: #ff0000;
  margin-right: 5px;
`

export interface FactTypes {
  type: string
  title: string
  value: string
}

export interface DataCollectionTypes {
  facts: FactTypes[]
  type: string
  collectionVariable: string
  collectionTitle: string
}

const initialData: DataCollectionTypes = {
  facts: [],
  type: 'DataCollectionSet',
  collectionVariable: '',
  collectionTitle: ''
}

interface DataCollectionSetProps {
  name: string
  id: string | number
  data: any
  onChange: () => {}
  variables: Variable[][]
  readOnly?: boolean
}

const Component = connect(({ name, variables, formik, readOnly = false }: DataCollectionSetProps & FormikValues) => {
  const intl = useIntl()

  let flattenedVariables = getAllFlattenedVariables(flattenDeep(variables))

  // If variable is an array, add the array item properties to the flattened variables
  for (const variable of flattenedVariables) {
    if (variable?.items?.properties) {
      const properties = variable.items.properties
      const propertyKeys = Object.keys(properties)
      if (!isEmpty(propertyKeys)) {
        for (const propertyKey of propertyKeys) {
          flattenedVariables.push({
            name: `${variable.name}[0].${propertyKey}`,
            displayName: properties[propertyKey]['x-displayName'] || propertyKey,
            type: properties[propertyKey].type,
            stepName: undefined,
            stepLabel: ''
          })
        }
      }

      flattenedVariables = sortBy(flattenedVariables, ['name'])
    }
  }

  const [showAllCollectionFields, setShowAllCollectionFields] = useState(true)

  const dataSetCollection = get(formik.values, name, { ...initialData })
  const initialSelectedVariable =
    dataSetCollection.collectionVariable &&
    flattenedVariables.find(
      variable =>
        variable.type === FieldTypes.ARRAY &&
        (variable.name === dataSetCollection.collectionVariable ||
          variable.name === dataSetCollection.collectionVariable.split('$.')[1]?.trim())
    )
  const [variableSelected, setVariableSelected] = useState<Variable | undefined>(initialSelectedVariable)
  const [allCollectionFieldsAvailable, setAllCollectionFieldsAvailable] = useState<FactTypes[] | undefined>()

  const resetAvailableFields = (selectedVariable: Variable | undefined, selectedFacts: FactTypes[]) => {
    if (selectedVariable) {
      const variableFields = flattenedVariables.filter(
        variable => startsWith(variable.name, selectedVariable.name) && variable.name !== selectedVariable.name
      )
      const dataCollectionItems = variableFields
        .map(variableField => {
          return { type: 'Fact', title: variableField.displayName, value: last(variableField.name.split('.')) }
        })
        .filter(variableField => !selectedFacts.some((fact: FactTypes) => fact.value === variableField.value))
      // @ts-expect-error
      setAllCollectionFieldsAvailable(dataCollectionItems)
    }
  }

  useEffect(() => {
    const facts = get(formik.values, `${name}.facts`, [])
    if (variableSelected) resetAvailableFields(variableSelected, facts)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variableSelected?.name])

  const handleVariableSelected = (selectedVariable: Variable | undefined) => {
    formik.setFieldValue(`${name}.collectionVariable`, selectedVariable?.name)
    formik.setFieldValue(`${name}.facts`, [])
    resetAvailableFields(selectedVariable, [])
    setVariableSelected(selectedVariable)
  }

  const toggleAccordion = () => {
    setShowAllCollectionFields(!showAllCollectionFields)
  }

  const updateCollectionSet = (field: FactTypes) => {
    const facts = get(formik.values, `${name}.facts`, [])
    const resultArray = cloneDeep(facts)
    if (
      dataSetCollection.facts?.some(
        (fact: FactTypes) => fact.value === field.value || fact.value === last(field.value.split('.'))
      )
    ) {
      pullAllWith(resultArray, [field], isEqual)
      formik.setFieldValue(`${name}.facts`, resultArray)
    } else {
      resultArray.push(field)
      formik.setFieldValue(`${name}.facts`, resultArray)
    }
    resetAvailableFields(variableSelected, resultArray)
  }

  const isFactInDataSetCollection = (field: FactTypes) => {
    return dataSetCollection.facts?.some(
      (fact: FactTypes) => fact.value === field.value || fact.value === last(field.value.split('.'))
    )
  }

  const displayEmptyDataCollectionSet = () => {
    return formik.touched?.stepData?.notifications[0]?.detail ? (
      <div>
        <StyledEmptyCollectionSet>{intl.formatMessage(messages.emptyDataCollection)}</StyledEmptyCollectionSet>
        {formik.errors?.stepData?.notifications[0]?.detail[0]?.facts &&
          formik.touched?.stepData?.notifications[0]?.detail[0]?.facts && (
            <StyledEmptyCollectionValidation className="field__error">
              <span>{formik.errors?.stepData?.notifications[0]?.detail[0]?.facts}</span>
            </StyledEmptyCollectionValidation>
          )}
      </div>
    ) : (
      <div>
        <StyledEmptyCollectionSet>{intl.formatMessage(messages.emptyDataCollection)}</StyledEmptyCollectionSet>
        <StyledEmptyCollectionValidation className="field__error">
          <span>Empty Collection Variable</span>
        </StyledEmptyCollectionValidation>
      </div>
    )
  }

  return (
    dataSetCollection && (
      <>
        <StyledDivider />
        <VariableInputContainer>
          <StyledRow>
            <StyledText
              disabled={readOnly}
              aria-label={intl.formatMessage(messages.collectionTitle)}
              name={`${name}.collectionTitle`}
              placeholder={intl.formatMessage(messages.collectionTitlePlaceholder)}
              required
              spellCheck
              label={intl.formatMessage(messages.collectionTitle)}
            />
          </StyledRow>
          <StyledRow>
            <StyledVariableInputTitle>
              <StyledRequiredSpan>{intl.formatMessage(messages.requiredStar)}</StyledRequiredSpan>
              {intl.formatMessage(messages.collectionToDisplay)}
            </StyledVariableInputTitle>
          </StyledRow>
          <StyledVariableAutoComplete
            readOnly={readOnly}
            name={`${name}.collectionVariable`}
            variables={variables}
            validVariableTypes={[FieldTypes.ARRAY, FieldTypes.ANY]}
            placeholder={intl.formatMessage(messages.collectionToDisplayPlaceholder)}
            onSelectOption={(variable: Variable) => handleVariableSelected(variable)}
            showValidationMessage={true}
          />
        </VariableInputContainer>
        {(variableSelected || dataSetCollection?.collectionVariable) && (
          <>
            <StyledFieldsTitle>{intl.formatMessage(messages.showCollectionFields)}</StyledFieldsTitle>
            {isEmpty(dataSetCollection.facts)
              ? displayEmptyDataCollectionSet()
              : dataSetCollection.facts?.map((field: FactTypes, index: number) => (
                  <DataCollectionFieldRow
                    key={`${field.value}-${index}`}
                    field={field}
                    isInDataSetCollection={isFactInDataSetCollection(field)}
                    updateCollectionSet={updateCollectionSet}
                    readOnly={readOnly}
                  />
                ))}
            {allCollectionFieldsAvailable && allCollectionFieldsAvailable.length > 0 && (
              <DataCollectionFieldsAccordion
                allFields={allCollectionFieldsAvailable}
                toggleAccordion={toggleAccordion}
                showCollectionFields={showAllCollectionFields}
                dataCollectionSet={dataSetCollection}
                updateCollectionSet={updateCollectionSet}
                readOnly={readOnly}
              />
            )}
          </>
        )}
      </>
    )
  )
})

const DataCollectionSet: AnyContentBlockTypes = { Component }

DataCollectionSet.displayName = 'DataCollectionSet'

DataCollectionSet.initialData = { ...initialData }

export default DataCollectionSet
